import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, Injector, PLATFORM_ID} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, firstValueFrom, of, Subscription} from 'rxjs';
import {catchError, distinctUntilChanged, map, skip, tap} from 'rxjs/operators';
import {AppConfigRestService} from '../../api/services/app-config.rest.service';
import {appChannelCode} from '../feature-flags/app-channel';
import {TokenDataProviderService} from '../services/token-data-provider.service';
import {JwtService} from '../services/jwt.service';

export interface MobileExperienceConfig {
  mobileShellEnabled: boolean;
  mobileTopNavigationEnabled: boolean;
  mobileVehicleWizardEnabled: boolean;
  mobileFlutterVehicleWizardEnabled: boolean;
  mobileFlutterSocialListEnabled: boolean;
  mobileFlutterMediaComposerEnabled: boolean;
  mobileEventCreationEnabled: boolean;
  mobileAdvertisementCreationEnabled: boolean;
  mobileCollaborationCreationEnabled: boolean;
}

export type MobileExperienceConfigStatus = 'loading' | 'ready' | 'failed';

const DISABLED_MOBILE_EXPERIENCE: Readonly<MobileExperienceConfig> = Object.freeze({
  mobileShellEnabled: false,
  mobileTopNavigationEnabled: false,
  mobileVehicleWizardEnabled: false,
  mobileFlutterVehicleWizardEnabled: false,
  mobileFlutterSocialListEnabled: false,
  mobileFlutterMediaComposerEnabled: false,
  mobileEventCreationEnabled: false,
  mobileAdvertisementCreationEnabled: false,
  mobileCollaborationCreationEnabled: false,
});

@Injectable({providedIn: 'root'})
export class MobileExperienceConfigService {
  private readonly configSubject = new BehaviorSubject<Readonly<MobileExperienceConfig>>(DISABLED_MOBILE_EXPERIENCE);
  private readonly statusSubject = new BehaviorSubject<MobileExperienceConfigStatus>('loading');
  private readonly subscriptions = new Subscription();
  private initialized = false;
  private initialLoad: Promise<void> | null = null;
  private requestSequence = 0;

  public readonly config$ = this.configSubject.asObservable().pipe(distinctUntilChanged());
  public readonly status$ = this.statusSubject.asObservable().pipe(distinctUntilChanged());

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly appConfig: AppConfigRestService,
    private readonly translate: TranslateService,
    private readonly injector: Injector,
  ) {
  }

  public initialize(): void {
    void this.initializeForBootstrap();
  }

  /**
   * Resolves the initial flag set before the first application layout is
   * rendered. This prevents the legacy desktop topbar from flashing on mobile
   * while the remote configuration is still loading.
   */
  public initializeForBootstrap(): Promise<void> {
    if (this.initialized || !isPlatformBrowser(this.platformId)) {
      return this.initialLoad || Promise.resolve();
    }
    this.initialized = true;
    this.injector.get(JwtService).getTokenFromStorage();
    this.initialLoad = firstValueFrom(this.loadCurrentConfig());
    this.subscriptions.add(this.translate.onLangChange.subscribe(() => this.refresh()));
    // Resolve after NativeSecureSession.initialize() has restored the WebView token.
    const tokenDataProvider = this.injector.get(TokenDataProviderService);
    this.subscriptions.add(tokenDataProvider.user$.pipe(
      map((user) => `${user?.['@id'] || ''}:${user?.email?.trim().toLowerCase() || ''}`),
      distinctUntilChanged(),
      skip(1),
    ).subscribe(() => this.refresh()));
    this.subscriptions.add(tokenDataProvider.sessionChanged$.subscribe(() => this.refresh()));
    return this.initialLoad;
  }

  public refresh(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.subscriptions.add(this.loadCurrentConfig().subscribe());
  }

  private loadCurrentConfig() {
    const sequence = ++this.requestSequence;
    this.statusSubject.next('loading');
    this.configSubject.next(DISABLED_MOBILE_EXPERIENCE);
    const locale = this.translate.currentLang || this.translate.defaultLang || 'pl-PL';
    return this.appConfig.getCurrent(appChannelCode(), locale, true).pipe(
      tap((config) => {
        if (sequence !== this.requestSequence) return;
        const mobileShellEnabled = !!config.mobileShellEnabled;
        this.configSubject.next({
          mobileShellEnabled,
          mobileTopNavigationEnabled: mobileShellEnabled && !!config.mobileTopNavigationEnabled,
          mobileVehicleWizardEnabled: !!config.mobileVehicleWizardEnabled,
          mobileFlutterVehicleWizardEnabled: !!config.mobileFlutterVehicleWizardEnabled,
          mobileFlutterSocialListEnabled: mobileShellEnabled && !!config.mobileFlutterSocialListEnabled,
          mobileFlutterMediaComposerEnabled: mobileShellEnabled && !!config.mobileFlutterMediaComposerEnabled,
          mobileEventCreationEnabled: mobileShellEnabled && !!config.mobileEventCreationEnabled,
          mobileAdvertisementCreationEnabled: mobileShellEnabled && !!config.mobileAdvertisementCreationEnabled,
          mobileCollaborationCreationEnabled: mobileShellEnabled && !!config.mobileCollaborationCreationEnabled,
        });
        this.statusSubject.next('ready');
      }),
      map(() => undefined),
      catchError(() => {
        if (sequence === this.requestSequence) {
          this.configSubject.next(DISABLED_MOBILE_EXPERIENCE);
          this.statusSubject.next('failed');
        }
        return of(undefined);
      }),
    );
  }

  public get current(): Readonly<MobileExperienceConfig> {
    return this.configSubject.value;
  }

  public get status(): MobileExperienceConfigStatus {
    return this.statusSubject.value;
  }
}
